// The icon cache in this browser: site icons (/icons/<host>) and images given by address (/icons/image/<address>)
// are kept in IndexedDB (client/lib/db.js), so a webtop seen before draws every icon without a single request. iconFor(src) answers
// with something an <img> can show: the stored image, or the logo for an icon known to be missing. Images are stored
// as data: URLs (base64), set straight as the <img>'s src: plain strings every browser keeps in IndexedDB (Safari has
// had trouble with Blobs there), with nothing to create or release; base64 is a third bigger, a few KB per icon.
// What IndexedDB lacks is fetched from webtop's icon proxy (server/routes/icons.js, which keeps nothing itself), at
// most FETCH_LIMIT at a time, and stored. Kept icons are refreshed in the background once old: found ones
// after HIT_TTL, missing ones after MISS_TTL (a site may have gained one). Without IndexedDB (a private window, a
// browser that refuses it) everything still works, only nothing is kept beyond the page.
import * as db from './db.js';

// The logo, every icon's fallback: the versioned address the page shell gives the favicon tag (server/lib/assets.js),
// else the icon proxy's /icons/default, the same file.
export const DEFAULT_ICON = document.querySelector('link[rel="icon"]')?.getAttribute('href') || '/icons/default';

// The store (client/lib/db.js), keyed by what the icon is of: a site's host (heliumdeploy.com, 192.168.10.111:8006) or, for
// an image given by address, that address (https://..., never a host); a value is {at, src: 'data:image/...;base64,...'}
// or {at, none: true}.
const STORE = db.ICONS;

// The key an icon path is kept under (see STORE).
function keyOf(src) {
  const image = src.startsWith('/icons/image/');
  try { return decodeURIComponent(src.slice(image ? '/icons/image/'.length : '/icons/'.length)); } catch { return src; }
}
const HIT_TTL = 30 * 24 * 3600 * 1000;
const MISS_TTL = 24 * 3600 * 1000;
const FETCH_LIMIT = 6;
const RETRY_WAIT = 1000; // the proxy answered "not found yet" (202): ask again after this
const RETRY_MAX = 15; // then give up for this page load (the logo shows; nothing is stored)

/**
 * Whether an icon path goes through this cache: a site's icon (/icons/<host>) or an image given by address. Not the
 * logo, nor an image an older webtop saved on the server (<sha1>.<ext>, served as it is and kept by the browser).
 */
export const isProxied = (src) => src.startsWith('/icons/image/')
  || (/^\/icons\/[^/]+$/.test(src) && src !== '/icons/default' && !/^\/icons\/[0-9a-f]{40}\.[a-z0-9]{2,4}$/.test(src));

const shown = new Map(); // src -> what to show (a data: URL or DEFAULT_ICON), for this page's life
const asking = new Map(); // src -> promise of it

/**
 * Drop icons from this browser's cache, for tiles that were deleted. Whether another tile still shows the same icon
 * is not checked: one that does simply fetches it again next time. Resolves once IndexedDB has let them go, so a
 * page about to leave can wait for it. Never rejects.
 */
export async function forgetIcons(srcs) {
  if (!srcs.length) return;
  for (const src of srcs) shown.delete(src);
  await db.remove(STORE, srcs.map(keyOf));
}

/** What an icon is known to show already on this page, so a redraw sets it at once; undefined when not known. */
export const knownIcon = (src) => shown.get(src);

/** Resolves to what an <img> should show for an icon path. Never rejects. */
export function iconFor(src) {
  if (shown.has(src)) return Promise.resolve(shown.get(src));
  if (!asking.has(src)) {
    asking.set(src, find(src).then((url) => { shown.set(src, url); asking.delete(src); return url; }));
  }
  return asking.get(src);
}

async function find(src) {
  const kept = await dbGet(keyOf(src));
  if (kept) {
    if (Date.now() - kept.at > (kept.none ? MISS_TTL : HIT_TTL)) refresh(src); // shown as it is meanwhile
    return show(kept);
  }
  const got = await download(src);
  if (!got) return DEFAULT_ICON; // the proxy could not say; asked again on the next page load
  dbPut(keyOf(src), got);
  return show(got);
}

const show = (entry) => (entry.none ? DEFAULT_ICON : entry.src);

// A newer copy for the next page load; this one keeps what it shows.
function refresh(src) {
  download(src).then((got) => { if (got) dbPut(keyOf(src), got); });
}

// Ask the proxy: {at, src} for an image (a data: URL), {at, none: true} when there is none, null when it never
// answered properly.
async function download(src) {
  for (let tries = 0; tries <= RETRY_MAX; tries++) {
    if (tries) await new Promise((resolve) => setTimeout(resolve, RETRY_WAIT));
    const res = await inTurn(() => fetch(src).then(async (r) => ({
      status: r.status,
      type: (r.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase(),
      bytes: r.status === 200 ? new Uint8Array(await r.arrayBuffer()) : null,
    }), () => null));
    if (!res) return null;
    if (res.status === 202) continue; // the server is still fetching it
    if (res.status === 200 && /^image\/[a-z0-9.+-]+$/.test(res.type) && res.bytes?.length) {
      return { at: Date.now(), src: `data:${res.type};base64,${base64(res.bytes)}` };
    }
    if (res.status === 404) return { at: Date.now(), none: true };
    return null;
  }
  return null;
}

// btoa takes a string of bytes; built in chunks, since one call with every byte as an argument overflows the stack.
function base64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

// At most FETCH_LIMIT requests out at once; a finished one hands its turn straight to the next in line.
let fetching = 0;
const turns = [];
async function inTurn(fn) {
  if (fetching < FETCH_LIMIT) fetching++;
  else await new Promise((resolve) => turns.push(resolve));
  try { return await fn(); } finally {
    const next = turns.shift();
    if (next) next(); else fetching--;
  }
}

// What is kept under a key (client/lib/db.js). Anything else (an older entry, a stray value) reads as not kept: it is
// fetched again and replaced.
async function dbGet(key) {
  const v = await db.get(STORE, key);
  const ok = v && typeof v.at === 'number' && (v.none === true || /^data:image\//.test(v.src ?? ''));
  return ok ? v : null;
}
// A refused write (a full disk) only means the icon is not kept.
const dbPut = (key, value) => db.put(STORE, key, value);
